import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '../../../Components/TanstackTable'
import useReportTable from '../useReportTable'
import { ContactData, ContactReportProps } from './types'
import { getContactOveralls } from './utils'

const ContactReportTable = (props: ContactReportProps) => {
    const { reportsData, isLoading } = props
    const { t } = useTranslation()

    const reports = useMemo(() => reportsData?.data || [], [reportsData])
    const months = useMemo(() => reportsData?.timePeriod || [], [reportsData])

    const columns = useMemo<ColumnDef<ContactData>[]>(() => {
        const dataSourceColumn: ColumnDef<ContactData> = {
            id: 'dataSource',
            header: t('app.common.dataSource'),
            enableSorting: false,
            accessorFn: (row) => row.dataSource?.name,
        }

        const monthColumns: ColumnDef<ContactData>[] = months?.map(({ month, monthName, year }) => ({
            id: `${month}/${year}`,
            header: monthName,
            enableSorting: false,
            cell: (info) => {
                const contactTotal = info.row.original.contactTotals?.find((ct) => ct?.month === month)
                if (!contactTotal) return null
                return <div className="text-center">{contactTotal.total}</div>
            },
        }))

        const totalColumn: ColumnDef<ContactData> = {
            id: 'dataSourceTotal',
            header: t('app.common.total'),
            enableSorting: false,
            cell: (info) => <div className="text-center fw-bolder">{info.row.original.contactGrandTotal}</div>,
        }

        return [dataSourceColumn, ...monthColumns, totalColumn]
    }, [months, t])

    const tableFooter = useMemo(() => {
        const { monthOveralls, grandOverall } = getContactOveralls(reports, months)

        return (
            <tr>
                <td>{t('app.common.overall')}</td>
                {monthOveralls.map((overall, i) => (
                    <td key={i} className="text-center">
                        {overall.count}
                    </td>
                ))}
                <td className="text-center fw-bolder">{grandOverall}</td>
            </tr>
        )
    }, [reports, months, t])

    const table = useReportTable(columns, reports)

    return (
        <DataTable
            table={table}
            tableFooter={tableFooter}
            isLoading={isLoading}
            theadClass="dmn-table-header"
        />
    )
}

export default ContactReportTable
